import { http } from "./http";

/**
 * Agent overview row — only the columns aivrmanager needs on the list page.
 * Mirrors AgentOverviewItemDto in the .NET AgentsController.
 */
export interface AgentListItem {
  agentId: number;
  agentRef: string;
  agentName: string;
  statusId: number;
  statusName: string;
  destination: string;
  voicemailEnabled: boolean;
  /** Customer and agent both have PBX on. */
  pbxEnabled: boolean;
  /** The PBX saw a SIP register for this agent within the PBX timeout. */
  pbxRegistered: boolean;
  /** 4-digit extension number (script 20); null when none is assigned. */
  pbxExtension: string | null;
  /** PBX login (unique across destinations); empty when none. */
  pbxUsername: string;
  /** Plain name as stored and exported; agentName may carry a "(nickname)" suffix. */
  name: string;
}

export interface AgentListResponse {
  items: AgentListItem[];
}

export interface AgentDestination {
  agentDestinationListId: number;
  destination: string;
  description: string;
}

/**
 * One DNIS that is implicitly linked to this agent because the agent's
 * <code>agentName</code> starts with the digits-only dnisPresentation.
 * <code>formattedNumber</code> mirrors the digits-only form so the SPA
 * can run it through <code>formatPhoneNumber()</code> for display.
 */
export interface LinkedDnis {
  dnisId: number;
  dnisPresentation: string;
  formattedNumber: string;
}

/**
 * Restricted agent detail — only the field-set agreed during the aivrmanager
 * porting interview (Generic name + email, Login, Phone settings, PBX
 * read-only block, Archive). Anything outside this set is preserved by
 * the backend on save but not rendered in the SPA.
 */
export interface AgentDetail {
  agentId: number;
  agentKey: string;
  agentName: string;
  emailAddress: string;
  username: string;
  statusId: number;
  statusName: string;
  destination: string;
  dialTimeout: number;
  cliTypeId: number;
  cliTypeName: string;
  voicemailEnabled: boolean;
  voicemailSendEmail: boolean;
  maxChannels: number;
  recordCalls: boolean;
  muteWhisper: boolean;
  agentSignoffSMS: string;

  pbxEnabled: boolean;
  /** Customer-level PBX flag; drives PBX section visibility so PBX can be
   *  toggled per agent even when this agent doesn't have it on yet. */
  customerPbxEnabled: boolean;
  pbxUsername: string;
  pbxPassword: string;
  pbxRegistered: boolean;
  pbxClientIP: string;
  mostRecentPbxRegisterUTC: string | null;

  /** PBX outbound identity: "hidden" | "number" | "denied" (no outbound calls, the default); null only before the DB script ran. */
  pbxIdentityMode: "hidden" | "number" | "from" | "denied" | null;
  pbxIdentityDnisId: number | null;
  /** Presentation of the chosen number; null when it is no longer an active number of the customer. */
  pbxIdentityNumber: string | null;
  /** False when mode is "number" but the number is gone: the UI warns and asks for a new choice. */
  pbxIdentityValid: boolean;
  /** False until the database has the identity columns (script 12-agent-pbx-identity.sql). */
  pbxIdentitySupported: boolean;

  /** 4-digit extension colleagues dial / transfer to on the PBX (script 20); null when none is assigned. */
  pbxExtension: string | null;
  /** Customer voice played as voicemail greeting before the beep; null = system prompt. */
  voicemailVoiceId: number | null;
  voicemailVoiceName: string | null;
  /** False until the database has the extension/greeting columns (script 20-pbx-internal-transfer.sql). */
  internalCallsSupported: boolean;

  destinations: AgentDestination[];
  /**
   * Numbers (DNIS) the AgentConnect script will route to this agent
   * because the agent's name is prefixed with the dnis. Empty when no
   * number routes here. The detail page shows a warning banner above
   * the name field when this list is non-empty.
   */
  linkedDnis: LinkedDnis[];
}

export interface AgentDetailUpdatePayload {
  agentName: string;
  emailAddress: string;
  username: string;

  statusId: number;
  destination: string;
  dialTimeout: number;
  cliTypeId: number;
  voicemailEnabled: boolean;
  voicemailSendEmail: boolean;
  maxChannels: number;
  recordCalls: boolean;
  muteWhisper: boolean;
  agentSignoffSMS: string;

  pbxEnabled: boolean;
  /** "hidden" | "number" | "denied"; omit/null to leave the stored identity untouched. */
  pbxIdentityMode?: "hidden" | "number" | "from" | "denied" | null;
  pbxIdentityDnisId?: number | null;

  /** 4-digit extension number; omit/null keeps the current one, "" clears it. */
  pbxExtension?: string | null;
  /** When true, voicemailVoiceId is stored (null = system prompt). */
  setVoicemailVoice?: boolean;
  voicemailVoiceId?: number | null;
}

/** An active number of the customer, selectable as PBX outbound identity. */
export interface IdentityNumber {
  dnisId: number;
  dnisPresentation: string;
  description: string;
}

// ---- Import / export ----

/** One destination as exported: the detail-screen fields plus reference and PBX login. */
export interface AgentExportRow {
  agentId: number;
  agentRef: string;
  agentName: string;
  emailAddress: string;
  username: string;
  statusId: number;
  statusName: string;
  destination: string;
  dialTimeout: number;
  cliTypeId: number;
  cliTypeName: string;
  voicemailEnabled: boolean;
  voicemailSendEmail: boolean;
  maxChannels: number;
  recordCalls: boolean;
  muteWhisper: boolean;
  agentSignoffSMS: string;
  pbxEnabled: boolean;
  pbxUsername: string;
  pbxPassword: string;
  /** 4-digit extension number (script 20); empty when none. */
  pbxExtension: string;
}

/**
 * One destination to import. Every field except agentName is optional:
 * null keeps the default a new destination gets. replaceAgentId archives
 * that existing destination first (duplicate → Replace).
 */
export interface ImportDestinationBody {
  agentName: string;
  emailAddress: string | null;
  username: string | null;
  statusId: number | null;
  destination: string | null;
  dialTimeout: number | null;
  cliTypeId: number | null;
  voicemailEnabled: boolean | null;
  voicemailSendEmail: boolean | null;
  maxChannels: number | null;
  recordCalls: boolean | null;
  muteWhisper: boolean | null;
  agentSignoffSMS: string | null;
  pbxEnabled: boolean | null;
  pbxUsername: string | null;
  pbxPassword: string | null;
  /** Optional 4-digit extension number (unique per customer); a taken number comes back as a warning. */
  pbxExtension: string | null;
  replaceAgentId: number | null;
}

export interface ImportDestinationResult {
  agentId: number;
  agentRef: string;
  agentName: string;
  pbxEnabled: boolean;
  pbxUsername: string;
  pbxPassword: string;
  replacedAgentId: number | null;
  /** Non-fatal issues, e.g. PBX requested while the customer has no PBX. */
  warnings: string[];
  /** Extension number after the import (empty when none or taken). */
  pbxExtension: string;
}

export interface AgentLookups {
  statuses: { id: number; name: string }[];
  cliTypes: { id: number; name: string }[];
}

export const agentsApi = {
  lookups: () => http<AgentLookups>("/api/agents/lookups"),
  exportRows: () => http<AgentExportRow[]>("/api/agents/export"),
  importOne: (body: ImportDestinationBody) =>
    http<ImportDestinationResult>("/api/agents/import", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  list: () => http<AgentListResponse>("/api/agents"),
  get: (id: number) => http<AgentDetail>(`/api/agents/${id}`),
  identityNumbers: () => http<IdentityNumber[]>("/api/agents/identity-numbers"),
  create: (agentName: string) =>
    http<AgentDetail>("/api/agents", {
      method: "POST",
      body: JSON.stringify({ agentName }),
    }),
  update: (id: number, payload: AgentDetailUpdatePayload) =>
    http<AgentDetail>(`/api/agents/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    }),
  changePassword: (id: number, newPassword: string) =>
    http<{ success: boolean }>(`/api/agents/${id}/password`, {
      method: "PUT",
      body: JSON.stringify({ newPassword }),
    }),
  addDestination: (
    id: number,
    payload: { destination: string; description: string },
  ) =>
    http<AgentDestination>(`/api/agents/${id}/destinations`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deleteDestination: (id: number, agentDestinationListId: number) =>
    http<void>(
      `/api/agents/${id}/destinations/${agentDestinationListId}`,
      { method: "DELETE" },
    ),
  activateDestination: (id: number, agentDestinationListId: number) =>
    http<AgentDetail>(
      `/api/agents/${id}/destinations/${agentDestinationListId}/activate`,
      { method: "POST" },
    ),
  archive: (id: number) =>
    http<{ success: boolean }>(`/api/agents/${id}/archive`, { method: "POST" }),
};
